import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Compass, BookOpen, PencilLine, Sprout, Check, ShieldCheck, Users, BarChart3, Coins } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { useSeo } from "../lib/seo";
import { ASSETS } from "../lib/assets";
import { api } from "../lib/api";
import { ISLANDS } from "../content/islands";
import { WorldMap, IslandGrid } from "../components/world-map/WorldMap";
import { LessonCard } from "../components/learning/LessonCard";
import { Loading, ErrorState } from "../components/common/States";
import { config } from "../lib/config";

function Hero() {
  const { t } = useI18n();
  return (
    <section data-testid="home-hero" className="relative isolate overflow-hidden">
      <img src={ASSETS.world.src} alt={ASSETS.world.alt} width={1280} height={720} fetchpriority="high" className="absolute inset-0 -z-20 h-full w-full object-cover object-[70%_center]" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-white/95 via-white/80 to-white/10 md:via-white/70" aria-hidden />
      <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 pb-20 pt-12 sm:px-6 md:grid-cols-[1.1fr_.9fr] lg:px-8 lg:pb-28 lg:pt-20">
        <div>
          <img src={ASSETS.logo.src} alt={ASSETS.logo.alt} width={500} height={500} className="reveal h-28 w-28 object-contain sm:h-36 sm:w-36" />
          <p className="reveal d1 mt-4 inline-flex rounded-full bg-[#FFD84A] px-4 py-1.5 text-xs font-extrabold uppercase tracking-wider text-[#17345F]">{t("home.eyebrow")}</p>
          <h1 data-testid="hero-headline" className="reveal d2 mt-4 font-heading text-5xl font-black leading-[1.1] tracking-tight text-[#123E91] sm:text-6xl lg:text-7xl" lang="zh-CN">{t("home.headline")}</h1>
          <p className="reveal d3 mt-5 text-xl font-bold text-[#17345F] sm:text-2xl" lang="zh-CN">{t("home.sub")}</p>
          <p className="reveal d3 mt-2 max-w-xl text-base text-[#17345F]/75 sm:text-lg">{t("home.subEn")}</p>
          <div className="reveal d4 mt-8 flex flex-wrap gap-3">
            <Link to="/world" data-testid="hero-explore-btn" className="btn-pill btn-primary px-6 py-3.5 text-base"><Compass className="h-5 w-5" />{t("home.cta1")}</Link>
            <a href="#mission" data-testid="hero-mission-btn" className="btn-pill btn-sun px-6 py-3.5 text-base">{t("home.cta2")}</a>
            <Link to="/community" data-testid="hero-community-btn" className="btn-pill btn-ghost px-6 py-3.5 text-base">{t("home.cta3")}</Link>
          </div>
        </div>
        <div className="relative hidden h-[420px] md:block" aria-hidden>
          <img src={ASSETS.explorer.src} alt="" width={260} height={260} className="reveal d2 animate-float absolute left-0 top-10 h-56 w-56 object-contain lg:h-64 lg:w-64" />
          <img src={ASSETS.pink.src} alt="" width={260} height={260} className="reveal d3 animate-float-slow absolute right-0 top-0 h-52 w-52 object-contain lg:h-60 lg:w-60" />
          <img src={ASSETS.scholar.src} alt="" width={260} height={260} className="reveal d4 animate-float absolute bottom-0 left-1/3 h-52 w-52 object-contain lg:h-60 lg:w-60" style={{ animationDelay: "1s" }} />
        </div>
      </div>
      <svg className="absolute -bottom-px left-0 w-full text-white" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden><path fill="currentColor" d="M0 50 C240 0 480 90 720 45 S1200 10 1440 50 V80 H0Z" /></svg>
    </section>
  );
}

function What() {
  const { t } = useI18n();
  return (
    <section id="mission" data-testid="home-what" className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:grid-cols-[1fr_1fr] lg:px-8 lg:py-24">
      <div>
        <h2 className="font-heading text-3xl font-black tracking-tight text-[#17345F] sm:text-4xl">{t("home.whatTitle")}</h2>
        <p className="mt-5 text-base leading-relaxed text-[#17345F]/80 sm:text-lg">{t("home.whatBody")}</p>
        <Link to="/about" data-testid="home-about-link" className="mt-6 inline-flex items-center gap-1 font-bold text-[#237BE8] hover:underline">{t("nav.about")} <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <ul className="grid gap-3 self-center">
        {t("home.whatPoints").map((p, i) => (
          <li key={p} className="reveal flex items-center gap-4 rounded-full bg-[#EAF7FF] py-3 pl-3 pr-6 font-bold text-[#17345F]" style={{ animationDelay: `${i * 80}ms`, marginLeft: `${(i % 2) * 28}px` }}>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-[#237BE8]"><Check className="h-5 w-5" /></span>{p}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Preview() {
  const { t } = useI18n();
  const nav = useNavigate();
  const go = (id) => nav(`/world?island=${id}`);
  return (
    <section data-testid="home-world-preview" className="bg-gradient-to-b from-white via-[#EAF7FF] to-white py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div><h2 className="font-heading text-3xl font-black text-[#17345F] sm:text-4xl">{t("home.previewTitle")}</h2><p className="mt-2 max-w-xl text-[#17345F]/75">{t("home.previewBody")}</p></div>
          <Link to="/world" data-testid="home-open-map-btn" className="btn-pill btn-primary">{t("home.previewCta")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <WorldMap onSelect={go} />
        <IslandGrid onSelect={go} />
      </div>
    </section>
  );
}

function Categories() {
  const { t } = useI18n();
  const subs = ["mathematics", "science", "languages", "coding", "reading", "arts", "geography", "culture"];
  return (
    <section data-testid="home-categories" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <h2 className="font-heading text-3xl font-black text-[#17345F] sm:text-4xl">{t("home.catTitle")}</h2>
      <div className="mt-8 flex flex-wrap gap-3">
        {subs.map((s, i) => {
          const isl = ISLANDS.find((x) => x.subjects.includes(s));
          const Icon = isl.icon;
          return (
            <Link key={s} to={`/learn?subject=${s}`} data-testid={`category-link-${s}`} className="card-float inline-flex items-center gap-3 rounded-full bg-white py-2.5 pl-2.5 pr-6 text-lg font-extrabold text-[#17345F]" style={{ transform: `rotate(${(i % 3) - 1}deg)` }}>
              <span className="grid h-11 w-11 place-items-center rounded-full" style={{ background: `linear-gradient(160deg, ${isl.from}, ${isl.to})` }}><Icon className="h-5 w-5 text-white" /></span>{t(`subjects.${s}`)}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function How() {
  const { t } = useI18n();
  const icons = [Compass, BookOpen, PencilLine, Sprout];
  return (
    <section data-testid="home-how" className="relative overflow-hidden bg-[#123E91] py-16 text-white lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-3xl font-black sm:text-4xl">{t("home.howTitle")}</h2>
        <ol className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {t("home.how").map(([h, b], i) => {
            const Icon = icons[i];
            return (
              <li key={h} className="reveal relative rounded-[28px] bg-white/10 p-6" style={{ animationDelay: `${i * 100}ms` }}>
                <span className="font-heading text-6xl font-black text-[#FFD84A]/30">0{i + 1}</span>
                <Icon className="mt-2 h-8 w-8 text-[#FFD84A]" />
                <p className="mt-3 font-heading text-xl font-extrabold">{h}</p>
                <p className="mt-1 text-sm text-white/80">{b}</p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

function Mascots() {
  const { t } = useI18n();
  const list = [["explorer", "#FFE3E0"], ["scholar", "#FFF4C8"], ["pink", "#DDF8F1"]];
  return (
    <section data-testid="home-mascots" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <h2 className="font-heading text-3xl font-black text-[#17345F] sm:text-4xl">{t("home.mascotTitle")}</h2>
      <div className="mt-10 grid gap-8 md:grid-cols-3">
        {list.map(([k, bg], i) => (
          <figure key={k} className={`reveal flex flex-col items-center text-center ${i === 1 ? "md:mt-12" : ""}`} style={{ animationDelay: `${i * 120}ms` }}>
            <div className="grid h-64 w-64 place-items-center rounded-full" style={{ background: bg }}>
              <img src={ASSETS[k].src} alt={ASSETS[k].alt} width={240} height={240} loading="lazy" className="h-60 w-60 object-contain transition-transform duration-300 hover:-rotate-3 hover:scale-105" />
            </div>
            <figcaption className="mt-5">
              <p className="font-heading text-2xl font-black text-[#17345F]">{t(`mascots.${k}`)[0]}</p>
              <p className="mx-auto mt-2 max-w-xs text-sm text-[#17345F]/70">{t(`mascots.${k}`)[1]}</p>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function Featured() {
  const { t } = useI18n();
  const [s, setS] = useState({ loading: true });
  const load = () => { setS({ loading: true }); api.get("/lessons", { params: { page_size: 3 } }).then(({ data }) => setS({ items: data.items })).catch(() => setS({ error: true })); };
  useEffect(load, []);
  return (
    <section data-testid="home-featured" className="bg-gradient-to-b from-[#FFF8DD] to-white py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div><h2 className="font-heading text-3xl font-black text-[#17345F] sm:text-4xl">{t("home.featuredTitle")}</h2><p className="mt-2 text-sm text-[#17345F]/65">{t("home.featuredNote")}</p></div>
          <Link to="/learn" data-testid="home-all-lessons-link" className="btn-pill btn-ghost">{t("nav.learn")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {s.loading && <Loading />}
        {s.error && <ErrorState onRetry={load} />}
        {s.items && <div className="grid gap-6 md:grid-cols-3">{s.items.map((l, i) => <LessonCard key={l.slug} lesson={l} i={i} />)}</div>}
      </div>
    </section>
  );
}

function Band({ id, icon: Icon, title, body, cta, to, img, flip }) {
  return (
    <div data-testid={`home-band-${id}`} className={`grid items-center gap-8 md:grid-cols-2 ${flip ? "md:[&>*:first-child]:order-2" : ""}`}>
      <div className="relative">
        <div className="absolute -inset-4 -z-10 rounded-[48px] bg-[#EAF7FF] rotate-[-2deg]" aria-hidden />
        <img src={img.src} alt={img.alt} width={img.w} height={img.h} loading="lazy" className={`w-full rounded-[36px] ${img.fit === "cover" ? "aspect-[16/10] object-cover" : "h-72 object-contain"}`} />
      </div>
      <div>
        <Icon className="h-10 w-10 text-[#237BE8]" />
        <h2 className="mt-3 font-heading text-3xl font-black text-[#17345F] sm:text-4xl">{title}</h2>
        <p className="mt-4 text-base leading-relaxed text-[#17345F]/80 sm:text-lg">{body}</p>
        <Link to={to} data-testid={`home-${id}-cta`} className="btn-pill btn-primary mt-6">{cta} <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </div>
  );
}

export default function Home() {
  const { t } = useI18n();
  useSeo(null);
  return (
    <div data-testid="home-page">
      <Hero />
      <What />
      <Preview />
      <Categories />
      <How />
      <Mascots />
      <Featured />
      <section className="mx-auto max-w-7xl space-y-24 overflow-x-clip px-4 py-20 sm:px-6 lg:px-8">
        <Band id="community" icon={Users} title={t("home.commTitle")} body={t("home.commBody")} cta={t("home.commCta")} to="/community" img={ASSETS.community} />
        <Band id="impact" icon={BarChart3} title={t("home.impactTitle")} body={t("home.impactBody")} cta={t("home.impactCta")} to="/impact" img={ASSETS.scholar} flip />
        <Band id="token" icon={Coins} title={t("home.tokenTitle")} body={t("home.tokenBody")} cta={t("home.tokenCta")} to="/token" img={ASSETS.logo} />
      </section>
      <section data-testid="home-final-cta" className="mx-4 overflow-hidden rounded-[48px] bg-gradient-to-br from-[#35A9F5] to-[#237BE8] sm:mx-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 px-6 py-16 text-center text-white md:flex-row md:text-left">
          <img src={ASSETS.pink.src} alt="" width={180} height={180} loading="lazy" className="animate-float h-40 w-40 object-contain" />
          <div className="flex-1">
            <h2 className="font-heading text-3xl font-black sm:text-4xl">{t("home.finalTitle")}</h2>
            <p className="mt-2 text-lg text-white/90">{t("home.finalBody")}</p>
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-white/80"><ShieldCheck className="h-4 w-4" />{config.taglineEn} · {config.taglineZh}</p>
          </div>
          <Link to="/world" data-testid="final-cta-btn" className="btn-pill btn-sun px-7 py-4 text-lg">{t("nav.cta")}</Link>
        </div>
      </section>
    </div>
  );
}
